<template>
<div>
    <div>
        <div class="homeview-section logo">
            <img v-if="$i18n.locale == 'se'" src="img/kunniakierros-logo-se.png" />
            <img v-else src="img/kunniakierros-logo-fi.png" />
        </div>

        <div class="homeview-section blue-section">
            <div v-if="store.selectedEvent" class="sponsor-count-circle">
                <div class="per-lap-sum">
                    {{ sumPerLap }}
                    <br>
                    <span class="per-lap-label">{{ "€" + $t("homeView.perLap") }}</span>
                </div>
            </div>
            <div v-if="store.selectedEvent" class="sponsor-payment-info">
                <div v-if="participatingSponsorCount > 0">
                    {{ $t("homeView.sponsorsParticipating", [ participatingSponsorCount ]) }}
                    {{ $t("homeView.totalSponsorPayments") }} {{ $n(totalSponsorPayments, "currency") }}
                </div>
                <div v-else>
                    {{ $t("homeView.addOrInviteSponsors") }}
                </div>
            </div>
            <div v-else>
                {{ $t("homeView.noEvent") }}
                <div v-if="!store.currentUser.organizations">
                    {{ $t("homeView.noOrganization") }}
                </div>
            </div>
        </div>


        <div v-if="store.selectedEvent" class="homeview-section lap-goal">
            <h2>{{ $t("homeView.event") }}</h2>
            <h3>{{ $t("homeView.eventInfo") }}</h3>
            <div>
                <button class="button button-green" @click="chooseOrganization()">
                    {{ store.selectedOrganization.name }}
                    <span class="icon-arrow-right2"></span>
                </button>
            </div>
            <div>
                <button class="button button-green" @click="chooseEvent()">
                    {{ store.selectedEvent.name }}
                    <span class="icon-arrow-right2"></span>
                </button>
            </div>
            <div>
                <button class="button button-green" @click="editLaps()">
                    {{ $t("homeView.repeats") }}
                    <span class="icon-arrow-right2"></span>
                </button>
            </div>
        </div>

        <div v-if="store.selectedEvent" class="homeview-section">
            <div>
                <button type="button" class="button button-green" @click="showSponsors()">
                    {{ $t("homeView.sponsors") }}
                </button>
            </div>
            <div>
                <button type="button" class="button button-green" @click="inviteSponsor()">
                    {{ $t("homeView.inviteSponsor") }}
                </button>
            </div>
        </div>

        <div class="homeview-section blue-section">
            <h2>{{ $t("homeView.infoTitle") }}</h2>
            <button type="button" class="button button-green" @click="permission()">
                {{ $t("homeView.permit") }}
            </button>

            <button type="button" class="button button-green" @click="instructions()">
                {{ $t("homeView.instructions") }}
            </button>
        </div>
    </div>
    <kk-popup />
</div>
</template>

<script lang="ts">
import axios from "axios";
import { store, User, Event, EventParticipant, Organization, Sponsor, GlobalSettings } from "store";
import { defineComponent } from "vue";
import { useI18n } from "vue-i18n";

export default defineComponent({
    mounted() {
        this.setTitle();
    },
    async setup() {
        const { t, locale } = useI18n({ useScope: "global" });

        let user;
        try {
            user = store.currentUser = (await axios.get<User>("users/me")).data;
            if (!store.currentUser.locale) {
                store.currentUser.locale = "fi";
            }
        } catch(err) {
            if (err.response.status == 401) {
                this.$router.push("/login");
            }
            return;
        }

        locale.value = store.currentUser.locale;

        let billingInfoSeen = localStorage.getItem("billingInfoSeen");
        if (!billingInfoSeen) {
            localStorage.setItem("billingInfoSeen", "indeed");
            store.popup.title = "";
            store.popup.description = t("homeView.billingInfo") as string;
            store.popup.visible = true;
        }

        store.currentUser.organizations = (await axios.get<Organization[]>("organizations")).data;
        if (store.currentUser.organizations.length === 0) {
            store.currentUser.organizations = null;
        }

        // pre-load previously persisted organization
        if (user.selectedOrganizationId) {
            store.selectedOrganization = user.organizations.find(o => o.organizationId == user.selectedOrganizationId);
        }

        if (!store.selectedOrganization && store.currentUser.organizations) {
            store.selectedOrganization = store.currentUser.organizations[0];
        }

        let events = (await axios.get<Event[]>("events", { params: { fromYear: new Date().getFullYear() } })).data;

        if (events.length == 0) {
            events = null;
        }

        if (events) {
            for (let e of events) {
                e.sponsors = (await axios.get<Sponsor[]>("sponsors", { params: { eventId: e.eventId } })).data
                    // do not include sponsors imported from external sources, as those are expected to be available
                    // in invitePreviousSponsorsView
                    .filter(s => !(s.participationStatus == "NotAnswered" && s.invitedOn == null))
            }

            store.events = events;

            store.selectedEvent = events.find(e => e.eventId == store.currentUser.selectedEventId);

            const selectedOrganizationEvents = store.events.filter(e => e.organizationId == store.currentUser.selectedOrganizationId);

            // Another organization was selected if the currently selected event isn't found in the
            // events of the currently selected organization.
            if (!store.selectedEvent || !selectedOrganizationEvents.find(e => e.eventId == store.selectedEvent.eventId)) {
                store.selectedEvent = store.events[0];
                store.selectedOrganization = store.currentUser.organizations.find(o => o.organizationId == store.selectedEvent.organizationId);
            }

            let eventParticipants = (await axios.get<EventParticipant[]>("eventparticipants", { params: { fromYear: new Date().getFullYear() } })).data;
            for (let ep of eventParticipants) {
                let event = events.find(e => e.eventId == ep.eventId);
                if (event) {
                    event.eventParticipant = ep;
                }
            }
        }

        store.globalSettings = (await axios.get<GlobalSettings>(
            "applicationsettings/globalsettings", {
                params: {
                    locale: locale.value,
                }
            }
        )).data;

        return {
            store: store
        };
    },
    computed: {
        sumPerLap(): string {
            let total = store.selectedEvent.sponsors
                .filter(s => s.paymentMethod === "PerRepeat" && s.participationStatus === "Participates")
                .reduce((totalSum, sponsor) => totalSum + sponsor.payment, 0);

            return total == 0 ? "?" : total.toString();
        },
        participatingSponsorCount() {
            return store.selectedEvent.sponsors
                .filter(s => s.participationStatus == "Participates" || s.participationStatus == "Paid")
                .length;
        },
        totalSponsorPayments() {
            return store.selectedEvent.sponsors
                .filter(s => s.participationStatus === "Participates")
                .reduce((totalSum, sponsor) => totalSum + sponsor.shouldPay, 0);
        }
    },
    methods: {
        setTitle() {
            store.page.title = this.$t("homeView.title") as string;
            store.page.showTopNav = false;
        },
        showSponsors() {
            this.$router.push("/sponsors");
        },
        chooseEvent() {
            this.$router.push("/choose-event");
        },
        chooseOrganization() {
            this.$router.push("/choose-organization");
        },
        editLaps() {
            this.$router.push("/edit-laps");
        },
        inviteSponsor() {
            this.$router.push("/invite-sponsor");
        },
        permission() {
            this.$router.push('/permission');
        },
        instructions() {
            this.$router.push('/instructions');
        }
    }
})

</script>